import React, { useState, useRef, useCallback } from 'react';

/**
 * Image Crop Modal Component
 * Professional image cropping modal for profile pictures
 * Features: Drag to reposition, fixed 1:1 aspect ratio
 */
const ImageCropModal = ({ imageUrl, onCropComplete, onCancel }) => {
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imageScale, setImageScale] = useState(1);
    const imageRef = useRef(null);
    const containerRef = useRef(null);

    // Load image and center it
    const handleImageLoad = useCallback(() => {
        if (imageRef.current && containerRef.current) {
            const img = imageRef.current;
            const container = containerRef.current;
            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;

            // Calculate scale to fit image (cover the crop area)
            const scaleX = containerWidth / img.naturalWidth;
            const scaleY = containerHeight / img.naturalHeight;
            const scale = Math.max(scaleX, scaleY);

            setImageScale(scale);
            
            // Center image
            const scaledWidth = img.naturalWidth * scale;
            const scaledHeight = img.naturalHeight * scale;
            setPosition({
                x: (containerWidth - scaledWidth) / 2,
                y: (containerHeight - scaledHeight) / 2
            });
        }
    }, []);

    // Mouse down handler
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    // Mouse move handler
    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return;

        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    }, [isDragging, dragStart]);

    // Mouse up handler
    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Crop and save
    const handleCrop = async () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = imageRef.current;
        const container = containerRef.current;

        // Set canvas size to desired output (400x400 for profile image)
        const outputSize = 400;
        canvas.width = outputSize;
        canvas.height = outputSize;

        // Calculate crop area (center square)
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        const cropSize = Math.min(containerWidth, containerHeight);
        const cropX = (containerWidth - cropSize) / 2;
        const cropY = (containerHeight - cropSize) / 2;

        // Calculate source coordinates on original image
        const sourceX = (cropX - position.x) / imageScale;
        const sourceY = (cropY - position.y) / imageScale;
        const sourceSize = cropSize / imageScale;

        // Draw cropped image
        ctx.drawImage(
            img,
            sourceX,
            sourceY,
            sourceSize,
            sourceSize,
            0,
            0,
            outputSize,
            outputSize
        );

        // Convert to blob
        canvas.toBlob((blob) => {
            onCropComplete(blob);
        }, 'image/jpeg', 0.95);
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <h3 style={styles.title}>Crop Profile Image</h3>
                    <button style={styles.closeBtn} onClick={onCancel}>
                        <i className='bx bx-x'></i>
                    </button>
                </div>

                <div 
                    ref={containerRef}
                    style={styles.cropContainer}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    <img
                        ref={imageRef}
                        src={imageUrl}
                        alt="Crop preview"
                        style={{
                            ...styles.image,
                            transform: `translate(${position.x}px, ${position.y}px) scale(${imageScale})`,
                            cursor: isDragging ? 'grabbing' : 'grab'
                        }}
                        onMouseDown={handleMouseDown}
                        onLoad={handleImageLoad}
                        draggable={false}
                    />
                    <div style={styles.cropOverlay}>
                        <div style={styles.cropCircle}></div>
                    </div>
                </div>

                <div style={styles.footer}>
                    <button className="btn btn-secondary" onClick={onCancel}>
                        Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleCrop}>
                        <i className='bx bx-check'></i>
                        Crop & Save
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        backdropFilter: 'blur(4px)'
    },
    modal: {
        background: '#fff',
        borderRadius: '1rem',
        width: '90%',
        maxWidth: '600px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem',
        borderBottom: '1px solid #e4e6ef'
    },
    title: {
        margin: 0,
        fontSize: '1.25rem',
        fontWeight: '600',
        color: '#181c32'
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        fontSize: '1.5rem',
        color: '#7f8c8d',
        cursor: 'pointer',
        padding: '0.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '0.5rem',
        transition: 'background 0.2s ease'
    },
    cropContainer: {
        position: 'relative',
        width: '100%',
        height: '400px',
        background: '#f5f8fa',
        overflow: 'hidden',
        userSelect: 'none'
    },
    image: {
        position: 'absolute',
        maxWidth: 'none',
        transformOrigin: 'top left',
        transition: 'transform 0.1s ease-out'
    },
    cropOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    cropCircle: {
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        border: '3px solid #3699ff',
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
        pointerEvents: 'none'
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '1rem',
        padding: '1.5rem'
    }
};

export default ImageCropModal;
